import React, { forwardRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useResponsiveLayout } from '../hooks/useResponsiveLayout';
import { MapLegend } from './MapLegend';
import RestaurantCountDisplay from './RestaurantCountDisplay';
import { XMarkIcon } from '@heroicons/react/24/outline';

interface FloatingLegendProps {
  isVisible: boolean;
  onClose?: () => void;
  nearbyRestaurants: any[];
  selectedCharger: any;
  chargerAvailability: any;
  onRestaurantClick?: (restaurant: any) => void;
  selectedRestaurantIndex: number | null;
  setSelectedRestaurantIndex: (index: number | null) => void;
  t: (key: any) => string;
  isAuthenticated?: boolean; // New prop to determine display mode
  onUpgradePrompt?: () => void; // Callback for upgrade/login prompt
  routingInfo?: Map<string, any>; // Navigation routing information
  onReportNonRestaurant?: (restaurant: any) => void;
  userPreferences?: {
    preferred_chains: string[];
    keywords: string[];
    search_level: string;
  } | null; // User's restaurant preferences
}

export const FloatingLegend = forwardRef<HTMLDivElement, FloatingLegendProps>(({
  isVisible,
  onClose,
  nearbyRestaurants,
  selectedCharger,
  chargerAvailability,
  onRestaurantClick,
  selectedRestaurantIndex,
  setSelectedRestaurantIndex,
  t,
  isAuthenticated = true,
  onUpgradePrompt,
  routingInfo,
  onReportNonRestaurant,
  userPreferences
}, ref) => {
  const { isMobile, isTablet, isDesktop } = useResponsiveLayout();

  // Debug logging for FloatingLegend
  console.log('🔧 FloatingLegend render - isVisible:', isVisible);
  console.log('🔧 FloatingLegend render - selectedCharger:', selectedCharger);
  console.log('🔧 FloatingLegend render - chargerAvailability:', chargerAvailability);
  console.log('🔧 FloatingLegend render - nearbyRestaurants length:', nearbyRestaurants?.length);

  // Animation variants based on viewport
  const getAnimationVariants = () => {
    if (isMobile) {
      // Mobile: slide up from bottom
      return {
        hidden: {
          y: '100%',
          opacity: 0,
          scale: 0.95
        },
        visible: {
          y: '0%',
          opacity: 1,
          scale: 1,
          transition: {
            type: 'spring' as const,
            damping: 25,
            stiffness: 200
          }
        },
        exit: {
          y: '100%',
          opacity: 0,
          scale: 0.95,
          transition: {
            duration: 0.3
          }
        }
      };
    } else if (isTablet) {
      // Tablet: slide in from left
      return {
        hidden: {
          x: '-100%',
          opacity: 0,
          scale: 0.95
        },
        visible: {
          x: '0%',
          opacity: 1,
          scale: 1,
          transition: {
            type: 'spring' as const,
            damping: 25,
            stiffness: 200
          }
        },
        exit: {
          x: '-100%',
          opacity: 0,
          scale: 0.95,
          transition: {
            duration: 0.3
          }
        }
      };
    } else {
      // Desktop: slide in from left (per use case)
      return {
        hidden: {
          x: '-100%',
          opacity: 0,
          scale: 0.95
        },
        visible: {
          x: '0%',
          opacity: 1,
          scale: 1,
          transition: {
            type: 'spring' as const,
            damping: 25,
            stiffness: 200
          }
        },
        exit: {
          x: '-100%',
          opacity: 0,
          scale: 0.95,
          transition: {
            duration: 0.3
          }
        }
      };
    }
  };

  // Calculate dynamic size based on restaurant count and content
  const getDynamicPositionClasses = () => {
    const restaurantCount = nearbyRestaurants.length;
    const hasSelectedRestaurant = selectedRestaurantIndex !== null;
    
    if (isMobile) {
      // Mobile: bottom, fixed heights to ensure scrollability
      let height = 'h-[30vh]'; // Fixed height instead of h-auto to force scroll area
      
      // If no restaurants, very small height
      if (restaurantCount === 0) {
        height = 'h-[20vh]'; 
      }
      // If expanded restaurant, allow more space
      else if (hasSelectedRestaurant) {
        height = 'h-[40vh]'; // Fixed height for expanded content
      }
      // For few restaurants, moderate height
      else if (restaurantCount <= 2) {
        height = 'h-[25vh]'; 
      }
      
      return `fixed bottom-0 left-0 right-0 ${height} z-30 touch-pan-y`;
    } else if (isTablet) {
      // Tablet: float over map from left, 35% width, positioned within map area
      return 'absolute top-4 left-4 bottom-4 w-[35vw] max-w-[400px] z-30';
    } else {
      // Desktop: float over map from left, 25% width, positioned within map area  
      return 'absolute top-4 left-4 bottom-4 w-[25vw] max-w-[350px] z-30';
    }
  };

  return (
    <AnimatePresence mode="wait">
      {isVisible && (
        <>
          {/* Backdrop overlay - only visible on mobile and only behind legend */}
          {isMobile && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black z-20 pointer-events-none"
              style={{ touchAction: 'none' }}
            />
          )}
          
          {/* Floating Legend Container */}
          <motion.div
            variants={getAnimationVariants()}
            initial="hidden"
            animate="visible"
            exit="exit"
            className={`
              ${getDynamicPositionClasses()}
              bg-white shadow-2xl border
              ${isMobile ? 'rounded-t-2xl border-t border-l border-r' : 'rounded-lg'}
              ${isTablet ? 'border-r' : ''}
              ${isDesktop ? 'border-r' : ''}
              overflow-hidden
            `}
            tabIndex={-1}
            style={{ outline: 'none' }}
            onTouchStart={(e) => {
              // Allow legend to capture touch events normally
              if (isMobile) {
                e.stopPropagation();
              }
            }}
            onTouchMove={(e) => {
              // Allow normal touch movement but prevent bubbling
              if (isMobile) {
                e.stopPropagation();
              }
            }}
            onWheel={(e) => {
              // Prevent wheel events from reaching the map on desktop
              e.stopPropagation();
            }}
          >
            {/* Header with close button and mobile supercharger info */}
            <div className={`
              flex items-center justify-between p-4 border-b bg-white
              ${isMobile ? 'pb-2 px-3' : ''}
            `}>
              {selectedCharger ? (
                /* All viewports: Show supercharger name + stalls in header */
                <div className="flex-1 min-w-0 mr-2">
                  <div className="flex items-center space-x-2">
                    <h3 className={`font-semibold text-gray-800 truncate ${isMobile ? 'text-sm' : 'text-base'}`} style={{ maxWidth: '25ch' }}>
                      {selectedCharger.name}
                    </h3>
                    {(chargerAvailability?.total !== null && chargerAvailability?.total !== undefined) ? (
                      <span className={`text-green-600 font-medium whitespace-nowrap ${isMobile ? 'text-xs' : 'text-sm'}`}>
                        ({chargerAvailability.total} {t('floatLegend_stalls')})
                      </span>
                    ) : (
                      <span className={`text-gray-500 font-medium whitespace-nowrap ${isMobile ? 'text-xs' : 'text-sm'}`}>
                        ({t('floatLegend_naStalls')})
                      </span>
                    )}
                    {/* Google Maps navigation link */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        const mapsUrl = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(selectedCharger.name + ' ' + selectedCharger.address)}`;
                        window.open(mapsUrl, '_blank');
                      }}
                      className={`text-blue-600 hover:text-blue-800 underline whitespace-nowrap ${isMobile ? 'text-xs' : 'text-sm'}`}
                      title={t('floatLegend_openInGoogleMaps')}
                    >
                      {t('floatLegend_maps')}
                    </button>
                  </div>
                </div>
              ) : (
                /* No charger selected: Show regular title */
                <h3 className="text-lg font-semibold text-gray-800">
                  {t('legend')}
                </h3>
              )}
              {onClose && (
                <button
                  onClick={onClose}
                  className="p-1 rounded-full hover:bg-gray-100 transition-colors flex-shrink-0"
                  aria-label={t('floatLegend_closeLegend')}
                >
                  <XMarkIcon className="w-5 h-5 text-gray-500" />
                </button>
              )}
            </div>

            {/* Scrollable content area */}
            <div 
              ref={ref}
              className={`
                overflow-y-auto p-4
                ${isMobile ? 'h-[calc(100%-60px)]' : 'h-[calc(100vh-60px)]'}
              `}
              tabIndex={-1}
              style={{ 
                outline: 'none',
                // Ensure proper touch scrolling on mobile
                WebkitOverflowScrolling: 'touch',
                overscrollBehavior: 'contain',
                touchAction: 'pan-y',
                overflowY: 'auto'
              }}
              onTouchStart={(e) => {
                // Allow normal scrolling but prevent bubbling to parent
                e.stopPropagation();
              }}
              onTouchMove={(e) => {
                // Allow normal scrolling but prevent bubbling to parent  
                e.stopPropagation();
              }}
              onScroll={(e) => {
                // Prevent scroll events from bubbling up
                e.stopPropagation();
              }}
            >
              {isAuthenticated ? (
                <MapLegend
                  nearbyRestaurants={nearbyRestaurants}
                  selectedCharger={selectedCharger}
                  chargerAvailability={chargerAvailability}
                  onRestaurantClick={onRestaurantClick}
                  selectedRestaurantIndex={selectedRestaurantIndex}
                  setSelectedRestaurantIndex={setSelectedRestaurantIndex}
                  t={t}
                  routingInfo={routingInfo}
                  onReportNonRestaurant={onReportNonRestaurant}
                  userPreferences={userPreferences}
                />
              ) : (
                <RestaurantCountDisplay
                  chargerName={selectedCharger?.name || t('floatLegend_selectedCharger')}
                  restaurantCount={nearbyRestaurants.length}
                  onUpgradePrompt={onUpgradePrompt || (() => {})}
                  t={t}
                />
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
});

FloatingLegend.displayName = 'FloatingLegend';

export default FloatingLegend;